import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen, UploadSimple, Trash, MagnifyingGlass, FileText,
  FilePdf, FileDoc, FileXls, FileCsv, CircleNotch, Check,
  Warning, Eye, X, Tag, Database, Globe, ArrowsClockwise
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'productinfo', label: 'Productinformatie' },
  { value: 'prijslijsten', label: 'Prijslijsten' },
  { value: 'technische_specs', label: 'Technische Specificaties' },
  { value: 'certificaten', label: 'Certificaten' },
  { value: 'faq', label: 'FAQ' },
  { value: 'handleidingen', label: 'Handleidingen' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'contracten', label: 'Contracten' },
  { value: 'overig', label: 'Overig' },
];

const CAT_COLORS = {
  productinfo: 'bg-blue-100 text-blue-700',
  prijslijsten: 'bg-emerald-100 text-emerald-700',
  technische_specs: 'bg-purple-100 text-purple-700',
  certificaten: 'bg-amber-100 text-amber-700',
  faq: 'bg-cyan-100 text-cyan-700',
  handleidingen: 'bg-indigo-100 text-indigo-700',
  marketing: 'bg-pink-100 text-pink-700',
  contracten: 'bg-zinc-100 text-zinc-700',
  overig: 'bg-zinc-100 text-zinc-600',
};

function FileIcon({ ext }) {
  if (ext === '.pdf') return <FilePdf size={18} className="text-red-500" />;
  if (['.docx', '.doc'].includes(ext)) return <FileDoc size={18} className="text-blue-500" />;
  if (['.xlsx', '.xls'].includes(ext)) return <FileXls size={18} className="text-emerald-500" />;
  if (ext === '.csv') return <FileCsv size={18} className="text-amber-500" />;
  return <FileText size={18} className="text-zinc-400" />;
}

export default function KnowledgeBasePage() {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadCat, setUploadCat] = useState('overig');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [urlAdding, setUrlAdding] = useState(false);
  const [urlResult, setUrlResult] = useState(null);
  const fileRef = useRef(null);
  // Detail
  const [selectedDoc, setSelectedDoc] = useState(null);
  // Search test
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const fetchDocs = useCallback(() => {
    const params = new URLSearchParams();
    if (catFilter) params.set('category', catFilter);
    if (search) params.set('search', search);
    fetch(`${API}/knowledge-base?${params}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setDocs(d.documents || []); setTotal(d.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [catFilter, search]);

  const fetchStats = useCallback(() => {
    fetch(`${API}/knowledge-base/stats`, { credentials: 'include' })
      .then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  // Orphan detection (KB-records waarvan het bestand op disk ontbreekt)
  const [orphans, setOrphans] = useState([]);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);

  const fetchOrphans = useCallback(() => {
    fetch(`${API}/knowledge-base/orphans`, { credentials: 'include' })
      .then(r => r.json()).then(d => setOrphans(d.orphans || [])).catch(() => {});
  }, []);

  const cleanupOrphans = async () => {
    if (orphans.length === 0) return;
    if (!window.confirm(`Verwijder ${orphans.length} orphan-record(s) (KB-documenten waarvan het bestand mist)?`)) return;
    setCleaningOrphans(true);
    try {
      const r = await fetch(`${API}/knowledge-base/orphans/cleanup`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: 'CLEANUP' }),
      });
      if (!r.ok) throw new Error('Cleanup mislukt');
      await Promise.all([fetchDocs(), fetchStats(), fetchOrphans()]);
    } catch (e) {
      alert(`Cleanup mislukt: ${e.message}`);
    } finally {
      setCleaningOrphans(false);
    }
  };

  useEffect(() => { fetchDocs(); fetchStats(); fetchOrphans(); }, [fetchDocs, fetchStats, fetchOrphans]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true); setUploadResult(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', uploadCat);
    formData.append('description', uploadDesc);
    try {
      const res = await fetch(`${API}/knowledge-base/upload?category=${uploadCat}&description=${encodeURIComponent(uploadDesc)}`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setUploadResult({ success: true, ...data });
        fetchDocs(); fetchStats();
        setUploadDesc('');
      } else {
        setUploadResult({ error: true, message: data.detail || 'Upload mislukt' });
      }
    } catch {
      setUploadResult({ error: true, message: 'Upload mislukt' });
    }
    setUploading(false);
  };

  const deleteDoc = async (docId) => {
    await fetch(`${API}/knowledge-base/${docId}`, { method: 'DELETE', credentials: 'include' });
    fetchDocs(); fetchStats();
    if (selectedDoc?.doc_id === docId) setSelectedDoc(null);
  };

  const handleAddUrl = async () => {
    if (!urlValue.trim()) return;
    setUrlAdding(true);
    setUrlResult(null);
    try {
      const res = await fetch(`${API}/knowledge-base/url`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlValue.trim(), category: uploadCat, description: uploadDesc }),
      });
      const data = await res.json();
      if (res.ok) {
        setUrlResult({ success: true, ...data });
        setUrlValue('');
        fetchDocs(); fetchStats();
      } else {
        setUrlResult({ error: true, message: data.detail || 'URL kon niet worden toegevoegd' });
      }
    } catch {
      setUrlResult({ error: true, message: 'Netwerkfout' });
    }
    setUrlAdding(false);
  };

  const refreshUrl = async (docId) => {
    try {
      const res = await fetch(`${API}/knowledge-base/${docId}/refresh`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setUrlResult({ success: true, refreshed: true, ...data });
        fetchDocs(); fetchStats();
      } else {
        setUrlResult({ error: true, message: data.detail || 'Verversen mislukt' });
      }
    } catch {
      setUrlResult({ error: true, message: 'Netwerkfout' });
    }
  };

  const viewDoc = async (docId) => {
    const res = await fetch(`${API}/knowledge-base/${docId}`, { credentials: 'include' });
    if (res.ok) setSelectedDoc(await res.json());
  };

  const testSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${API}/knowledge-base/search`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery }),
      });
      setSearchResults(await res.json());
    } catch { /* ignore */ }
    setSearching(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="knowledge-base-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Kennisbank</h1>
        <p className="text-sm text-zinc-500 mt-1">Upload documenten zodat de AI nauwkeurig kan antwoorden op emails met jullie eigen informatie</p>
      </div>

      {orphans.length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-sm flex items-start gap-3" data-testid="kb-orphans-banner">
          <Trash size={20} className="text-amber-700 flex-shrink-0 mt-0.5" weight="bold" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-amber-900">
              {orphans.length} orphan-record{orphans.length === 1 ? '' : 's'} gedetecteerd
            </div>
            <div className="text-amber-800 mt-0.5">
              Deze records hebben geen bestand meer op disk (vermoedelijk uit een gedeeltelijke restore).
              RAG-zoeken werkt nog via de geïndexeerde tekst, maar downloads falen.
              Upload eerst de juiste bestanden opnieuw, daarna kun je deze records opruimen:
            </div>
            <ul className="mt-2 text-xs text-amber-800 space-y-0.5 max-h-32 overflow-y-auto">
              {orphans.map(o => (
                <li key={o.doc_id} className="font-mono">• {o.filename || o.doc_id}</li>
              ))}
            </ul>
          </div>
          <button
            onClick={cleanupOrphans}
            disabled={cleaningOrphans}
            className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-sm hover:bg-amber-700 disabled:opacity-50 flex-shrink-0"
            data-testid="kb-orphans-cleanup-btn"
          >
            {cleaningOrphans ? 'Bezig...' : 'Orphan records opruimen'}
          </button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="kb-stats">
          {[
            { l: 'Documenten', v: stats.total_documents, c: 'text-zinc-700' },
            { l: 'Geindexeerd', v: stats.indexed_documents, c: 'text-emerald-600' },
            { l: 'Woorden', v: stats.total_words?.toLocaleString('nl-NL'), c: 'text-blue-600' },
            { l: 'Grootte', v: `${stats.total_size_mb} MB`, c: 'text-purple-600' },
            { l: 'Categorieen', v: Object.keys(stats.by_category || {}).length, c: 'text-amber-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white p-3 text-center">
              <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Upload + Search */}
        <div className="space-y-5">
          {/* Upload */}
          <div className="border border-zinc-200 bg-white p-5 rounded-sm">
            <div className="flex items-center gap-2 mb-3">
              <UploadSimple size={18} weight="bold" className="text-[#0055FF]" />
              <h3 className="text-sm font-semibold">Document Uploaden</h3>
            </div>
            <div className="space-y-3 mb-3">
              <Select value={uploadCat} onValueChange={setUploadCat}>
                <SelectTrigger className="rounded-sm text-sm" data-testid="upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} placeholder="Beschrijving (optioneel)" className="rounded-sm text-sm" data-testid="upload-desc" />
            </div>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer?.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#0055FF] bg-blue-50' : 'border-zinc-300 hover:border-zinc-400'}`}
              data-testid="kb-dropzone"
            >
              <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.csv,.md" className="hidden"
                onChange={e => handleUpload(e.target.files?.[0])} />
              {uploading ? (
                <div className="flex items-center gap-2 justify-center text-[#0055FF]"><CircleNotch size={18} className="animate-spin" /> Uploaden & indexeren...</div>
              ) : (
                <>
                  <UploadSimple size={24} className="mx-auto mb-1 text-zinc-300" />
                  <div className="text-xs text-zinc-500">PDF, DOCX, XLSX, TXT, CSV, MD</div>
                </>
              )}
            </div>
            {uploadResult && (
              <div className={`mt-3 p-3 rounded-sm text-xs ${uploadResult.error ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`} data-testid="upload-result">
                {uploadResult.error ? uploadResult.message : (
                  <div><Check size={12} className="inline mr-1" /> <strong>{uploadResult.filename}</strong> — {uploadResult.word_count} woorden, {uploadResult.chunk_count} chunks geindexeerd</div>
                )}
              </div>
            )}
          </div>

          {/* URL toevoegen */}
          <div className="border border-zinc-200 bg-white p-5 rounded-sm" data-testid="kb-url-card">
            <div className="flex items-center gap-2 mb-3">
              <Globe size={18} weight="bold" className="text-[#0055FF]" />
              <h3 className="text-sm font-semibold">Website / URL toevoegen</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              Voeg een publieke webpagina toe. De inhoud wordt automatisch opgehaald,
              geschoond en geïndexeerd — net als een upload. Gebruikt de categorie + beschrijving hierboven.
            </p>
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="kb-url-input"
                value={urlValue}
                onChange={e => setUrlValue(e.target.value)}
                placeholder="https://www.horseappcompare.com/products"
                className="rounded-sm text-sm flex-1"
                onKeyDown={e => e.key === 'Enter' && handleAddUrl()}
              />
              <button
                data-testid="kb-url-add-btn"
                onClick={handleAddUrl}
                disabled={urlAdding || !urlValue.trim()}
                className="px-3 py-2 text-xs font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1 shrink-0"
              >
                {urlAdding ? <CircleNotch size={12} className="animate-spin" /> : <Globe size={12} />}
                {urlAdding ? 'Ophalen...' : 'Toevoegen'}
              </button>
            </div>
            {urlResult && (
              <div
                data-testid="kb-url-result"
                className={`p-3 rounded-sm text-xs ${urlResult.error ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}
              >
                {urlResult.error ? urlResult.message : (
                  <div>
                    <Check size={12} className="inline mr-1" />
                    <strong>{urlResult.filename}</strong>
                    {urlResult.updated || urlResult.refreshed ? ' (bijgewerkt)' : ' toegevoegd'}
                    {' — '}{urlResult.word_count} woorden, {urlResult.chunk_count} chunks geïndexeerd
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Search test */}
          <div className="border border-zinc-200 bg-white p-5 rounded-sm">
            <div className="flex items-center gap-2 mb-3">
              <MagnifyingGlass size={18} weight="bold" className="text-[#0055FF]" />
              <h3 className="text-sm font-semibold">Kennisbank Zoeken</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Test hoe de AI relevante informatie vindt in je documenten.</p>
            <div className="flex gap-2 mb-3">
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Bijv: PE-buis 63mm specificaties" className="rounded-sm text-sm" data-testid="kb-search-input"
                onKeyDown={e => e.key === 'Enter' && testSearch()} />
              <button onClick={testSearch} disabled={searching} data-testid="kb-search-btn"
                className="px-3 py-2 text-xs font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 shrink-0">
                {searching ? <CircleNotch size={12} className="animate-spin" /> : <MagnifyingGlass size={12} />}
              </button>
            </div>
            {searchResults && (
              <div className="space-y-2">
                <div className="text-xs text-zinc-400">{searchResults.results?.length || 0} resultaten</div>
                {searchResults.results?.map((r, i) => (
                  <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-sm p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium">{r.filename}</span>
                      <Badge className={`rounded-sm px-1.5 py-0.5 text-[9px] ${CAT_COLORS[r.category] || CAT_COLORS.overig}`}>{r.category}</Badge>
                      <span className="text-[10px] text-zinc-400 ml-auto">score: {r.score}</span>
                    </div>
                    <div className="text-[11px] text-zinc-600 line-clamp-3">{r.text?.slice(0, 300)}...</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Documents list */}
        <div className="lg:col-span-2">
          {/* Filter bar */}
          <div className="flex items-center gap-3 mb-4">
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek documenten..."
              className="rounded-sm text-sm max-w-xs" data-testid="kb-filter-search" />
            <Select value={catFilter || 'all'} onValueChange={v => setCatFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48 rounded-sm text-sm"><SelectValue placeholder="Alle categorieen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle categorieen</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-zinc-400 ml-auto">{total} documenten</span>
          </div>

          {/* Document list */}
          <div className="border border-zinc-200 overflow-hidden rounded-sm" data-testid="kb-documents">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Bestand</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Categorie</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Woorden</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Chunks</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Datum</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Acties</th>
                </tr>
              </thead>
              <tbody>
                {docs.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-400">
                    <BookOpen size={32} className="mx-auto mb-2 text-zinc-300" />
                    <div className="text-sm">Geen documenten. Upload je eerste document.</div>
                  </td></tr>
                ) : docs.map(d => (
                  <tr key={d.doc_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {d.source_type === 'url' ? (
                          <Globe size={20} className="text-[#0055FF] shrink-0" weight="bold" />
                        ) : (
                          <FileIcon ext={d.extension} />
                        )}
                        <div>
                          <div className="font-medium text-sm flex items-center gap-1.5">
                            {d.filename}
                            {d.source_type === 'url' && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-medium rounded-sm bg-blue-50 text-blue-700">
                                URL
                              </span>
                            )}
                          </div>
                          {d.source_url && (
                            <a
                              href={d.source_url} target="_blank" rel="noopener noreferrer"
                              data-testid={`kb-source-link-${d.doc_id}`}
                              className="text-[10px] text-zinc-500 hover:text-[#0055FF] underline truncate inline-block max-w-md"
                            >
                              {d.source_url}
                            </a>
                          )}
                          {!d.source_url && d.description && <div className="text-[10px] text-zinc-400">{d.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${CAT_COLORS[d.category] || CAT_COLORS.overig}`}>
                        {CATEGORIES.find(c => c.value === d.category)?.label || d.category}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-center font-data text-xs">{d.word_count?.toLocaleString('nl-NL') || '-'}</td>
                    <td className="px-4 py-2.5 text-center font-data text-xs">{d.chunk_count || '-'}</td>
                    <td className="px-4 py-2.5 text-xs text-zinc-500">{d.created_at ? new Date(d.created_at).toLocaleDateString('nl-NL') : '-'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {d.source_type === 'url' && (
                          <button
                            onClick={() => refreshUrl(d.doc_id)}
                            data-testid={`kb-refresh-${d.doc_id}`}
                            className="p-1 hover:bg-blue-50 rounded-sm"
                            title="Ververs (haal de pagina opnieuw op)"
                          >
                            <ArrowsClockwise size={14} className="text-zinc-500 hover:text-[#0055FF]" />
                          </button>
                        )}
                        <button onClick={() => viewDoc(d.doc_id)} className="p-1 hover:bg-zinc-100 rounded-sm" title="Bekijken"><Eye size={14} className="text-zinc-500" /></button>
                        <button onClick={() => deleteDoc(d.doc_id)} className="p-1 hover:bg-red-50 rounded-sm" title="Verwijderen"><Trash size={14} className="text-zinc-400 hover:text-red-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedDoc && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDoc(null)}>
          <div className="bg-white rounded-sm shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="kb-detail">
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileIcon ext={selectedDoc.extension} />
                <div>
                  <div className="font-medium">{selectedDoc.filename}</div>
                  <div className="text-xs text-zinc-400">{selectedDoc.word_count} woorden, {selectedDoc.chunk_count} chunks, {(selectedDoc.file_size / 1024).toFixed(0)} KB</div>
                </div>
              </div>
              <button onClick={() => setSelectedDoc(null)} className="p-1.5 hover:bg-zinc-100 rounded-sm"><X size={18} /></button>
            </div>
            <div className="px-6 py-5">
              <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Geextraheerde tekst (preview)</div>
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-xs text-zinc-600 whitespace-pre-wrap max-h-96 overflow-y-auto font-mono leading-relaxed">
                {selectedDoc.extracted_text?.slice(0, 5000) || 'Geen tekst geextraheerd'}
                {selectedDoc.extracted_text?.length > 5000 && '\n\n... (afgekapt)'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info banner */}
      <div className="mt-6 p-3 bg-blue-50 border border-blue-200 text-sm text-blue-800 flex items-center gap-2">
        <Database size={16} className="shrink-0" />
        <span>Documenten in de kennisbank worden automatisch gebruikt door de AI bij het genereren van antwoorden op inkomende emails.</span>
      </div>
    </div>
  );
}
